import { getUserErrorMessage } from '@packages/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

const GOOGLE_PAY_ENABLED = true;
const GOOGLE_PAY_SCRIPT_URL = 'https://pay.google.com/gp/p/js/pay.js';

// Broad request used only to detect device/browser compatibility before the
// Worldline config arrives; the actual payment uses the config's networks.
const DEVICE_COMPATIBILITY_REQUEST: GooglePayIsReadyToPayRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'],
      },
    },
  ],
};

type GooglePayButtonStatus =
  // Detecting device/browser compatibility — render nothing
  | 'checking'
  // Not compatible, or an availability check failed — render nothing
  | 'unavailable'
  // Compatible device, waiting on config + availability — render a placeholder
  | 'loading'
  // Confirmed available — render the real button
  | 'ready';

export type GooglePayAuthorizationResult =
  | { status: 'SUCCESS' }
  | { status: 'REDIRECT' }
  | { status: 'FAILED'; errorMessage: string };

type GooglePayButtonProps = {
  environment: GooglePayEnvironment;
  // null while the payment config is still loading
  paymentDataRequest: GooglePayPaymentDataRequest | null;
  authorizePayment: (paymentData: GooglePayPaymentData) => Promise<GooglePayAuthorizationResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
  onCancel?: () => void;
};

const loadGooglePayScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof google !== 'undefined' && google.payments?.api) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_PAY_SCRIPT_URL}"]`,
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Pay')),
        {
          once: true,
        },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_PAY_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Pay'));
    document.head.appendChild(script);
  });
};

const isGooglePayCancellation = (error: unknown): boolean => {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { statusCode?: unknown }).statusCode === 'CANCELED'
  );
};

const buildIsReadyToPayRequest = (
  paymentDataRequest: GooglePayPaymentDataRequest,
): GooglePayIsReadyToPayRequest => {
  return {
    apiVersion: paymentDataRequest.apiVersion,
    apiVersionMinor: paymentDataRequest.apiVersionMinor,
    allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods.map(({ type, parameters }) => ({
      type,
      parameters,
    })),
  };
};

export const GooglePayButton = ({
  environment,
  paymentDataRequest,
  authorizePayment,
  onSuccess,
  onError,
  onSubmitting,
  onCancel,
}: GooglePayButtonProps) => {
  const [status, setStatus] = useState<GooglePayButtonStatus>('checking');
  const paymentsClientRef = useRef<GooglePayPaymentsClient | null>(null);
  const buttonContainerRef = useRef<HTMLDivElement | null>(null);

  const handleClick = useCallback(async () => {
    const paymentsClient = paymentsClientRef.current;
    if (!paymentsClient || !paymentDataRequest) return;

    try {
      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
      onSubmitting?.();

      const result = await authorizePayment(paymentData);

      if (result.status === 'SUCCESS') {
        onSuccess();
        return;
      }

      // The browser is navigating to the bank's 3DS challenge page
      if (result.status === 'REDIRECT') {
        return;
      }

      onError(result.errorMessage);
    } catch (error) {
      if (isGooglePayCancellation(error)) {
        onCancel?.();
        return;
      }

      onError(getUserErrorMessage(error));
    }
  }, [authorizePayment, onCancel, onError, onSubmitting, onSuccess, paymentDataRequest]);

  // Detect device compatibility up front (without waiting for the payment
  // config) so a placeholder can be shown while the real button loads
  useEffect(() => {
    if (!GOOGLE_PAY_ENABLED) return;

    let isCancelled = false;

    const checkDeviceCompatibility = async () => {
      try {
        await loadGooglePayScript();

        if (isCancelled) return;

        const paymentsClient = new google.payments.api.PaymentsClient({ environment });
        const response = await paymentsClient.isReadyToPay(DEVICE_COMPATIBILITY_REQUEST);

        if (isCancelled) return;

        paymentsClientRef.current = paymentsClient;
        setStatus(response.result ? 'loading' : 'unavailable');
      } catch (error) {
        // Google Pay unavailable — the card flow remains
        console.warn('Google Pay initialization failed:', error);
        if (!isCancelled) setStatus('unavailable');
      }
    };

    void checkDeviceCompatibility();

    return () => {
      isCancelled = true;
    };
  }, [environment]);

  // Once the payment config arrives, confirm availability with its networks
  useEffect(() => {
    const paymentsClient = paymentsClientRef.current;
    if (status !== 'loading' || !paymentsClient || !paymentDataRequest) return;

    let isCancelled = false;

    const confirmAvailability = async () => {
      try {
        const response = await paymentsClient.isReadyToPay(
          buildIsReadyToPayRequest(paymentDataRequest),
        );

        if (isCancelled) return;

        setStatus(response.result ? 'ready' : 'unavailable');
      } catch (error) {
        // Google Pay unavailable — the card flow remains
        console.warn('Google Pay availability check failed:', error);
        if (!isCancelled) setStatus('unavailable');
      }
    };

    void confirmAvailability();

    return () => {
      isCancelled = true;
    };
  }, [status, paymentDataRequest]);

  useEffect(() => {
    const paymentsClient = paymentsClientRef.current;
    const container = buttonContainerRef.current;

    if (status !== 'ready' || !paymentsClient || !container) return;

    const button = paymentsClient.createButton({
      onClick: () => {
        void handleClick();
      },
      buttonSizeMode: 'fill',
      buttonType: 'plain',
    });

    container.replaceChildren(button);

    return () => {
      container.replaceChildren();
    };
  }, [handleClick, status]);

  if (status === 'checking' || status === 'unavailable') {
    return null;
  }

  return status === 'loading' ? (
    <Skeleton className="h-12 w-full rounded-full" />
  ) : (
    <div ref={buttonContainerRef} className="h-12 w-full" />
  );
};
